import * as React from "react";
import { ComponentProps, ReactNode } from "react";
import { styled } from "styled-components";

import { VisuallyHidden } from "../../elements/visuallyHidden";
import { Heading } from "../../structures/heading";

const SummaryItemLabel = styled.dt`
  flex: 0 0 auto;
  margin-right: 0.5rem;
  font-weight: bold;
  width: calc(100% / 5 * 2);
  max-width: 12rem;
`;

const SummaryItemValue = styled.dd`
  flex: 1 1 auto;
  overflow: auto;
  word-break: break-word;
`;

const SummaryList = styled.dl<{ $tight?: boolean }>`
  padding: 0;
  margin: 0;
  list-style: none;
  ${({ $tight }) => ($tight ? `${SummaryItemLabel} { width: auto }` : "")}
`;

const SummaryItemInner: React.FC<
  React.HTMLAttributes<HTMLDivElement> &
    React.PropsWithChildren<{ label: string; className?: string; hiddenLabel?: boolean }>
> = ({ label, hiddenLabel = false, children, ...props }) => (
  <div key={label} {...props}>
    {hiddenLabel ? (
      <VisuallyHidden>
        <SummaryItemLabel>{label}</SummaryItemLabel>
      </VisuallyHidden>
    ) : (
      <SummaryItemLabel>{label}</SummaryItemLabel>
    )}
    <SummaryItemValue aria-label={label}>{children}</SummaryItemValue>
  </div>
);

const SummaryItem = styled(SummaryItemInner)<{ $tight?: boolean }>`
  display: flex;
  margin-bottom: ${({ $tight }) => ($tight ? "0" : "1")}rem;
`;

const SectionEl = styled.section`
  margin-bottom: 2rem;
`;

const SummarySection: React.FC<ComponentProps<typeof SectionEl> & { title: string; actions?: ReactNode }> = ({
  title,
  actions,
  children,
  ...props
}) => {
  return (
    <SectionEl {...props}>
      {(title || actions) && (
        <Heading>
          <Heading.SectionTitle>{title}</Heading.SectionTitle>
          <Heading.Actions>{actions}</Heading.Actions>
        </Heading>
      )}
      {children}
    </SectionEl>
  );
};

export const Summary = Object.freeze({ Item: SummaryItem, List: SummaryList, Section: SummarySection });
